import { useState } from 'react';
import { SignedScreenshot } from '@/components/feedback/SignedScreenshot';

/**
 * A thumbnail row rendering ALL of a feedback report's attached images (R5
 * shipped up to 5 per report; this is the R3-display-side close-out —
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, cross-ring gap).
 *
 * Before this component, every display surface (MySubmissionsTab,
 * CoordinationTab) rendered only the single legacy `screenshot_url` — a
 * client could attach 5 screenshots and see only 1 rendered back, on both
 * their own submission list AND the coordinator's cross-tenant view.
 *
 * Back-compat (R5 AC5): a pre-R5 report has no `feedback_images` rows, so
 * `images` is `[]` and only the legacy `screenshot_url` carries the
 * attachment. `legacyUrl` is used ONLY as a fallback when `images` is empty —
 * `images[0]` already IS the legacy image for any R5+ report (R5 dual-writes
 * position 0 to the legacy `screenshot_ref` column), so rendering both would
 * duplicate the first thumbnail.
 *
 * `maxVisible` (visual-review finding, 2026-07-24 final pass): in a narrow
 * context (the client Popover's card list), up to 5 variable-width ~96px-tall
 * thumbnails wrap raggedly and inflate row height. When set, only the first
 * `maxVisible` thumbnails render, with a "+N more" affordance that expands to
 * the full set on click. Omitted (the default, and the drawer/CoordinationTab
 * usage) — every image renders, unchanged from prior behavior.
 */
interface GalleryImage {
  url: string;
  position: number;
}

interface ImageGalleryProps {
  /** The full per-report image list (own images only, position-ordered). */
  images: GalleryImage[] | undefined | null;
  /** Legacy single-image fallback — rendered ONLY when `images` is empty. */
  legacyUrl?: string | null;
  /** Base alt text; a multi-image gallery suffixes it with "N of M". */
  alt: string;
  /** data-testid on the wrapping row; each thumbnail gets `${testId}-item`. */
  testId?: string;
  imgClassName?: string;
  linkClassName?: string;
  /** Cap the number of thumbnails shown before a "+N more" affordance. */
  maxVisible?: number;
}

export function ImageGallery({
  images,
  legacyUrl,
  alt,
  testId,
  imgClassName,
  linkClassName,
  maxVisible,
}: ImageGalleryProps) {
  const [expanded, setExpanded] = useState(false);

  const shots: GalleryImage[] =
    images && images.length > 0
      ? images
      : legacyUrl
        ? [{ url: legacyUrl, position: 0 }]
        : [];

  if (shots.length === 0) return null;

  const isCapped = maxVisible !== undefined && !expanded && shots.length > maxVisible;
  const visibleShots = isCapped ? shots.slice(0, maxVisible) : shots;
  const hiddenCount = shots.length - visibleShots.length;

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={testId}>
      {visibleShots.map((img) => (
        <SignedScreenshot
          key={img.position}
          url={img.url}
          alt={shots.length > 1 ? `${alt} (${img.position + 1} of ${shots.length})` : alt}
          testId={testId ? `${testId}-item` : undefined}
          imgClassName={imgClassName}
          linkClassName={linkClassName}
        />
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          data-testid={testId ? `${testId}-more` : undefined}
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-xs font-medium text-slate-600 hover:bg-slate-100"
          onClick={() => setExpanded(true)}
        >
          +{hiddenCount} more
        </button>
      )}
    </div>
  );
}
